// aifab - Lineage Page
// Full experiment audit trail per usecase: tree + detail panel
import { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../hooks/apiFetch';
import { useApi } from '../hooks/useApi';
import { Spinner, StatusBadge } from '../components/common';

// ── Palette ──────────────────────────────────────────────────────────────────
const OR   = 'var(--or)';
const BD   = '#C8D5E8';
const BG2  = '#E4EAF4';
const BG3  = '#F1F5FB';
const TX   = '#0F172A';
const TX2  = '#334155';
const TX3  = '#64748B';
const GREEN= 'var(--green, #22c55e)';
const RED  = 'var(--red, #ef4444)';
const GOLD = '#f59e0b';

const PURPOSE_COLOR = {
  CLASSIFICATION: '#3b82f6',
  REGRESSION:     '#8b5cf6',
  NER:            '#ec4899',
  DOMAIN_PRETRAIN:'#22c55e',
  CUSTOM:         '#6b7280',
};
const PURPOSE_LABEL = {
  CLASSIFICATION: 'Classification',
  REGRESSION:     'Regression',
  NER:            'NER',
  DOMAIN_PRETRAIN:'Domain Pre-train',
  CUSTOM:         'Custom',
};

// ── Metric pill ───────────────────────────────────────────────────────────────
function MetricPill({ metric }) {
  if (!metric) return null;
  return (
    <span style={{
      fontFamily:'var(--f-mono)', fontSize:10, fontWeight:700,
      background:'rgba(232,71,10,.1)', color: OR,
      padding:'2px 7px', borderRadius:3, border:`1px solid rgba(232,71,10,.2)`,
      whiteSpace:'nowrap',
    }}>
      {metric.key} {metric.display}
    </span>
  );
}

// ── Tree node ─────────────────────────────────────────────────────────────────
function TreeNode({ run, selectedId, onSelect, children, isRoot }) {
  const isSelected  = run.id === selectedId;
  const isChampion  = run.is_champion;
  const hasChildren = children && children.length > 0;

  return (
    <div style={{ position:'relative' }}>
      {/* Connector line from parent */}
      {!isRoot && (
        <div style={{
          position:'absolute', left:-20, top:16,
          width:20, height:1, background: BD,
        }} />
      )}

      {/* Node */}
      <div
        onClick={() => onSelect(run)}
        style={{
          display:'flex', alignItems:'center', gap:8,
          padding:'7px 10px', borderRadius:6, cursor:'pointer',
          border:`1.5px solid ${isSelected ? OR : isChampion ? GOLD : BD}`,
          background: isSelected ? 'rgba(232,71,10,.06)' : isChampion ? 'rgba(245,158,11,.04)' : BG2,
          marginBottom:4, position:'relative',
          transition:'all .15s',
          boxShadow: isChampion && !isSelected ? `0 0 0 1px rgba(245,158,11,.3)` : 'none',
        }}
      >
        {isChampion && (
          <span style={{ fontSize:12, position:'absolute', top:-8, right:6,
            background: GOLD, color:'#000', borderRadius:3,
            padding:'0 5px', fontSize:9, fontWeight:700, letterSpacing:'.05em',
          }}>★ CHAMPION</span>
        )}
        <span style={{ fontFamily:'var(--f-mono)', fontSize:12, color: TX3, minWidth:28 }}>
          #{run.run_number}
        </span>
        <StatusBadge code={run.status} />
        <span style={{ fontSize:13, color: TX2, flex:1, minWidth:0,
          whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
          {run.run_label || `Run #${run.run_number}`}
        </span>
        {run.datasource_version_num != null && (
          <span style={{ fontSize:11, color: TX3, fontFamily:'var(--f-mono)' }}>
            DS v{run.datasource_version_num}
          </span>
        )}
        <MetricPill metric={run.primary_metric} />
        {hasChildren && (
          <span style={{ fontSize:11, color: TX3, marginLeft:2 }}>
            +{children.length}
          </span>
        )}
      </div>

      {/* Children */}
      {hasChildren && (
        <div style={{ marginLeft:28, paddingLeft:12,
          borderLeft:`1px solid ${BD}`, marginBottom:4 }}>
          {children}
        </div>
      )}
    </div>
  );
}

// ── Build tree from flat list ─────────────────────────────────────────────────
function buildTree(runs, parentId = null) {
  return runs
    .filter(r => (r.parent_run_id || null) === parentId)
    .map(r => ({
      run: r,
      children: buildTree(runs, r.id),
    }));
}

function renderTree(nodes, selectedId, onSelect, isRoot = true) {
  return nodes.map(({ run, children }) => (
    <TreeNode key={run.id} run={run} selectedId={selectedId} onSelect={onSelect}
      isRoot={isRoot}>
      {renderTree(children, selectedId, onSelect, false)}
    </TreeNode>
  ));
}

// ── FK ID badge ───────────────────────────────────────────────────────────────
function FkId({ label, id }) {
  if (!id) return null;
  return (
    <div style={{ display:'flex', gap:6, alignItems:'center', marginBottom:3 }}>
      <span style={{ fontSize:11, color: TX3, minWidth:120, flexShrink:0 }}>{label}</span>
      <span style={{ fontFamily:'var(--f-mono)', fontSize:11, color: TX3,
        background: BG3, padding:'1px 6px', borderRadius:3, border:`1px solid ${BD}` }}>
        {id.slice(0,8)}…<span style={{opacity:.4}}>{id.slice(-4)}</span>
      </span>
    </div>
  );
}

// ── Section heading ───────────────────────────────────────────────────────────
function SectionHead({ label }) {
  return (
    <div style={{ fontSize:11, fontWeight:700, letterSpacing:'.1em',
      textTransform:'uppercase', color: TX3, marginBottom:8, marginTop:14,
      paddingBottom:4, borderBottom:`1px solid ${BD}` }}>
      {label}
    </div>
  );
}

// ── Detail panel ──────────────────────────────────────────────────────────────
function DetailPanel({ run, usecase, onChampion, onUnchampion, canChampion }) {
  if (!run) return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center',
      justifyContent:'center', height:'100%', gap:12, color: TX3 }}>
      <div style={{ fontSize:32, opacity:.3 }}>◈</div>
      <div style={{ fontSize:13 }}>Select a run to view its audit trail</div>
    </div>
  );

  const dref  = run.datasource_ref  || {};
  const mref  = run.model_config_ref || {};
  const fsnap = run.feature_config_snapshot || {};
  const psnap = run.params_snapshot || {};
  const isChampion = run.is_champion;

  // Metric rows by purpose
  const metrics = [
    run.f1_score        != null && { k:'F1 Score',       v:`${(run.f1_score*100).toFixed(2)}%` },
    run.accuracy        != null && { k:'Accuracy',       v:`${(run.accuracy*100).toFixed(2)}%` },
    run.precision_score != null && { k:'Precision',      v:`${(run.precision_score*100).toFixed(2)}%` },
    run.recall_score    != null && { k:'Recall',         v:`${(run.recall_score*100).toFixed(2)}%` },
    run.mae             != null && { k:'MAE',            v:run.mae.toFixed(4) },
    run.mse             != null && { k:'MSE',            v:run.mse.toFixed(4) },
    run.rmse            != null && { k:'RMSE',           v:run.rmse.toFixed(4) },
    run.r2_score        != null && { k:'R²',             v:run.r2_score.toFixed(4) },
    run.mlm_loss        != null && { k:'MLM Loss',       v:run.mlm_loss.toFixed(4) },
    run.perplexity      != null && { k:'Perplexity',     v:run.perplexity.toFixed(4) },
  ].filter(Boolean);

  return (
    <div style={{ height:'100%', overflowY:'auto', padding:'16px 18px' }}>

      {/* Header */}
      <div style={{ display:'flex', alignItems:'flex-start', gap:10, marginBottom:12 }}>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
            <span style={{ fontFamily:'var(--f-mono)', fontSize:13, fontWeight:700, color: OR }}>
              #{run.run_number}
            </span>
            <StatusBadge code={run.status} />
            {isChampion && (
              <span style={{ background: GOLD, color:'#000', fontSize:9, fontWeight:800,
                padding:'2px 8px', borderRadius:3, letterSpacing:'.08em' }}>
                ★ CHAMPION
              </span>
            )}
          </div>
          <div style={{ fontSize:12, color: TX2, marginTop:3, wordBreak:'break-word' }}>
            {run.run_label || `Run #${run.run_number}`}
          </div>
        </div>
        {/* Champion actions */}
        {canChampion && run.status === 'COMPLETED' && (
          isChampion ? (
            <button className="btn btn-ghost btn-sm" style={{ flexShrink:0, fontSize:10 }}
              onClick={() => onUnchampion()}>
              ✕ Unset Champion
            </button>
          ) : (
            <button className="btn btn-ghost btn-sm" style={{ flexShrink:0, fontSize:10,
              borderColor: GOLD, color: GOLD }}
              onClick={() => onChampion(run.id)}>
              ★ Set Champion
            </button>
          )
        )}
      </div>

      {/* FK audit IDs */}
      <SectionHead label="Audit Trail — FK References" />
      <FkId label="run_id"                 id={run.id} />
      <FkId label="usecase_id"             id={run.usecase_id} />
      <FkId label="datasource_id"          id={run.datasource_id} />
      <FkId label="datasource_version_id"  id={run.datasource_version_id} />
      <FkId label="model_version_id"       id={run.model_version_id} />
      <FkId label="latest_job_id"          id={run.latest_job_id} />

      {/* Dataset */}
      <SectionHead label="Dataset" />
      <div style={{ display:'flex', flexWrap:'wrap', gap:'4px 20px', marginBottom:4 }}>
        <span style={{ fontSize:11 }}><span style={{ color: TX3 }}>Name: </span>{dref.display_name || '—'}</span>
        {dref.version_number != null &&
          <span style={{ fontSize:11 }}><span style={{ color: TX3 }}>Version: </span>
            <span style={{ color: OR, fontWeight:600 }}>v{dref.version_number}</span></span>}
        {dref.total_rows != null &&
          <span style={{ fontSize:11 }}><span style={{ color: TX3 }}>Rows: </span>
            {Number(dref.total_rows).toLocaleString()}</span>}
      </div>
      {dref.content_hash &&
        <div style={{ fontSize:9, color: TX3, fontFamily:'var(--f-mono)', marginBottom:2 }}>
          hash: {dref.content_hash.slice(0,16)}…
        </div>}
      {dref.file_path &&
        <div style={{ fontSize:9, color: TX3, fontFamily:'var(--f-mono)', wordBreak:'break-all' }}>
          {dref.file_path}
        </div>}

      {/* Model */}
      <SectionHead label="Model" />
      <div style={{ display:'flex', flexWrap:'wrap', gap:'4px 20px', marginBottom:4 }}>
        <span style={{ fontSize:11 }}><span style={{ color: TX3 }}>Family: </span>{mref.family_name || '—'}</span>
        <span style={{ fontSize:11 }}><span style={{ color: TX3 }}>Base: </span>{mref.base_model_name || '—'}</span>
        {mref.version_number != null &&
          <span style={{ fontSize:11 }}><span style={{ color: TX3 }}>Version: </span>
            <span style={{ color: OR }}>v{mref.version_number}</span></span>}
      </div>

      {/* Metrics */}
      {metrics.length > 0 && <>
        <SectionHead label="Results" />
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'4px 12px' }}>
          {metrics.map(m => (
            <div key={m.k} style={{ display:'flex', justifyContent:'space-between',
              padding:'4px 8px', background: BG3, borderRadius:4, border:`1px solid ${BD}` }}>
              <span style={{ fontSize:10, color: TX3 }}>{m.k}</span>
              <span style={{ fontSize:11, fontWeight:700, fontFamily:'var(--f-mono)', color: OR }}>
                {m.v}
              </span>
            </div>
          ))}
        </div>
      </>}

      {/* Artifact */}
      {run.artifact_path && <>
        <SectionHead label="Artifact" />
        <div style={{ fontSize:10, color: GREEN, fontFamily:'var(--f-mono)',
          wordBreak:'break-all', padding:'6px 8px', background: BG3, borderRadius:4,
          border:`1px solid rgba(34,197,94,.2)` }}>
          {run.artifact_server_name}: {run.artifact_path}
        </div>
      </>}

      {/* Feature config */}
      {(fsnap.target_column || fsnap._config_name) && <>
        <SectionHead label="Feature Config" />
        {fsnap._config_name &&
          <div style={{ fontSize:11, marginBottom:3 }}>
            <span style={{ color: TX3 }}>Config: </span>{fsnap._config_name}
          </div>}
        {fsnap.target_column &&
          <div style={{ fontSize:11, marginBottom:3 }}>
            <span style={{ color: TX3 }}>Target: </span>
            <span style={{ fontFamily:'var(--f-mono)', color: OR }}>{fsnap.target_column}</span>
          </div>}
      </>}

      {/* Params snapshot */}
      {Object.keys(psnap).length > 0 && <>
        <SectionHead label="Hyperparameters" />
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'3px 10px' }}>
          {Object.entries(psnap).slice(0, 12).map(([k, v]) => (
            <div key={k} style={{ display:'flex', justifyContent:'space-between', gap:4,
              padding:'3px 8px', background: BG3, borderRadius:3, border:`1px solid ${BD}` }}>
              <span style={{ fontSize:9, color: TX3, minWidth:0, overflow:'hidden',
                textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{k}</span>
              <span style={{ fontSize:9, fontFamily:'var(--f-mono)', color: TX2,
                flexShrink:0 }}>{String(v)}</span>
            </div>
          ))}
        </div>
      </>}

      {/* Clone lineage */}
      {run.parent_run_id && <>
        <SectionHead label="Lineage" />
        <div style={{ fontSize:11, marginBottom:4 }}>
          <span style={{ color: TX3 }}>Cloned from </span>
          <span style={{ color: OR, fontWeight:600 }}>Run #{run.run_number}</span>
          <span style={{ fontFamily:'var(--f-mono)', fontSize:9, color: TX3, marginLeft:6 }}>
            {run.parent_run_id.slice(0,8)}…
          </span>
        </div>
      </>}

      {/* Execution attempts */}
      {run.attempts?.length > 0 && <>
        <SectionHead label="Execution Attempts" />
        {run.attempts.map((j, i) => (
          <div key={j.job_id} style={{ marginBottom:8, padding:'8px 10px',
            background: BG3, borderRadius:5, border:`1px solid ${BD}` }}>
            <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap', marginBottom:4 }}>
              <span style={{ fontSize:10, color: TX3 }}>Attempt {j.attempt_number}</span>
              <StatusBadge code={j.status} />
              {j.server_name && <span style={{ fontSize:10, color: TX2 }}>🖥 {j.server_name}</span>}
              {j.duration_secs != null &&
                <span style={{ fontSize:11, color: TX3, fontFamily:'var(--f-mono)' }}>
                  {j.duration_secs}s
                </span>}
            </div>
            <div style={{ display:'flex', flexWrap:'wrap', gap:'2px 16px' }}>
              <FkId label="job_id"       id={j.job_id} />
              <FkId label="server_id"    id={j.server_id} />
              <FkId label="datasource_id"id={j.datasource_id} />
              <FkId label="version_id"   id={j.version_id} />
            </div>
            {j.error_class && (
              <div style={{ marginTop:4, fontSize:10, color: RED }}>
                {j.error_class}{j.error_message ? `: ${j.error_message.slice(0,120)}` : ''}
              </div>
            )}
          </div>
        ))}
      </>}

    </div>
  );
}

// ── Create usecase modal ──────────────────────────────────────────────────────
function CreateUsecaseModal({ projectId, onClose, onCreated }) {
  const [name,    setName]    = useState('');
  const [desc,    setDesc]    = useState('');
  const [purpose, setPurpose] = useState('CLASSIFICATION');
  const [saving,  setSaving]  = useState(false);
  const [err,     setErr]     = useState(null);

  const submit = async () => {
    if (!name.trim()) { setErr('Name is required'); return; }
    setSaving(true); setErr(null);
    try {
      const uc = await apiFetch('/usecases', {
        method: 'POST',
        body: JSON.stringify({ project_id: projectId, usecase_name: name.trim(),
          description: desc || null, run_purpose: purpose }),
      });
      onCreated(uc);
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.6)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex:999 }}
      onClick={onClose}>
      <div style={{ background: BG2, border:`1px solid ${BD}`, borderRadius:10,
        padding:24, width:400, maxWidth:'90vw' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ fontWeight:700, fontSize:14, marginBottom:16 }}>New Usecase</div>
        {err && <div style={{ color: RED, fontSize:12, marginBottom:10 }}>{err}</div>}
        <div className="form-grp">
          <label className="form-lbl">Name</label>
          <input className="form-inp" value={name} onChange={e => setName(e.target.value)}
            placeholder="e.g. Customer Churn, Invoice Classification" />
        </div>
        <div className="form-grp">
          <label className="form-lbl">Purpose</label>
          <select className="form-inp" value={purpose} onChange={e => setPurpose(e.target.value)}>
            {Object.entries(PURPOSE_LABEL).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        </div>
        <div className="form-grp">
          <label className="form-lbl">Description (optional)</label>
          <textarea className="form-inp" value={desc} onChange={e => setDesc(e.target.value)}
            rows={2} placeholder="What problem does this usecase solve?" />
        </div>
        <div className="modal-actions" style={{ marginTop:16 }}>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary btn-sm" onClick={submit} disabled={saving}>
            {saving ? <Spinner size={12}/> : 'Create Usecase'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function LineagePage({ projectId, user }) {
  const [usecases,     setUsecases]     = useState(null);
  const [activeUc,     setActiveUc]     = useState(null);
  const [lineage,      setLineage]      = useState(null);
  const [lineageLoad,  setLineageLoad]  = useState(false);
  const [selectedRun,  setSelectedRun]  = useState(null);
  const [showCreate,   setShowCreate]   = useState(false);
  const [championing,  setChampioning]  = useState(false);

  // Load usecases for project
  const loadUsecases = useCallback(async () => {
    if (!projectId) return;
    try {
      const list = await apiFetch(`/usecases?project_id=${projectId}`);
      setUsecases(list || []);
      if (!activeUc && list?.length > 0) setActiveUc(list[0]);
    } catch (_) { setUsecases([]); }
  }, [projectId]);

  useEffect(() => { loadUsecases(); }, [loadUsecases]);

  // Load lineage when active usecase changes
  useEffect(() => {
    if (!activeUc) return;
    setLineageLoad(true);
    setSelectedRun(null);
    apiFetch(`/usecases/${activeUc.id}/lineage`)
      .then(d => setLineage(d))
      .catch(() => setLineage(null))
      .finally(() => setLineageLoad(false));
  }, [activeUc?.id]);

  const handleChampion = async (runId) => {
    if (!activeUc) return;
    setChampioning(true);
    try {
      await apiFetch(`/usecases/${activeUc.id}/champion?run_id=${runId}`, { method:'POST' });
      // Reload lineage - champion flag changed
      const d = await apiFetch(`/usecases/${activeUc.id}/lineage`);
      setLineage(d);
      setSelectedRun(prev => prev ? { ...prev, is_champion: prev.id === runId } : prev);
    } catch (e) { alert(e.message); }
    finally { setChampioning(false); }
  };

  const handleUnchampion = async () => {
    if (!activeUc) return;
    setChampioning(true);
    try {
      await apiFetch(`/usecases/${activeUc.id}/champion`, { method:'DELETE' });
      const d = await apiFetch(`/usecases/${activeUc.id}/lineage`);
      setLineage(d);
      setSelectedRun(prev => prev ? { ...prev, is_champion: false } : prev);
    } catch (e) { alert(e.message); }
    finally { setChampioning(false); }
  };

  if (!projectId) return (
    <div className="page" style={{ display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ textAlign:'center', color: TX3 }}>
        <div style={{ fontSize:32, opacity:.3, marginBottom:12 }}>⎇</div>
        <div>Select a project to view experiment lineage</div>
      </div>
    </div>
  );

  const trees = lineage ? buildTree(lineage.runs) : [];

  return (
    <div className="page" style={{ display:'flex', flexDirection:'column', height:'100%', padding:0 }}>

      {/* ── Top bar ─────────────────────────────────────────────── */}
      <div style={{ padding:'12px 20px', borderBottom:`1px solid ${BD}`,
        display:'flex', alignItems:'center', gap:12, flexShrink:0 }}>
        <div style={{ fontWeight:700, fontSize:14 }}>Lineage</div>
        <div style={{ fontSize:11, color: TX3 }}>Experiment audit trail by usecase</div>
        <div style={{ flex:1 }} />
        <button className="btn btn-primary btn-sm" onClick={() => setShowCreate(true)}>
          + New Usecase
        </button>
      </div>

      <div data-tour="lineage-graph" style={{ display:'flex', flex:1, minHeight:0, overflow:'hidden' }}>

        {/* ── Usecase sidebar ─────────────────────────────────────── */}
        <div style={{ width:220, borderRight:`1px solid ${BD}`, overflowY:'auto',
          flexShrink:0, padding:'10px 8px' }}>
          {usecases === null ? (
            <div style={{ display:'flex', justifyContent:'center', padding:24 }}>
              <Spinner size={18} />
            </div>
          ) : usecases.length === 0 ? (
            <div style={{ padding:'20px 12px', textAlign:'center', color: TX3, fontSize:11 }}>
              No usecases yet.<br/>
              <span style={{ color: OR, cursor:'pointer' }}
                onClick={() => setShowCreate(true)}>Create one</span>
            </div>
          ) : usecases.map(uc => {
            const isActive = activeUc?.id === uc.id;
            const pc = PURPOSE_COLOR[uc.run_purpose] || '#6b7280';
            return (
              <div key={uc.id}
                onClick={() => setActiveUc(uc)}
                style={{
                  padding:'9px 10px', borderRadius:6, cursor:'pointer', marginBottom:3,
                  background: isActive ? 'rgba(232,71,10,.07)' : 'transparent',
                  border:`1px solid ${isActive ? OR : 'transparent'}`,
                  transition:'all .12s',
                }}>
                <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                  <span style={{ width:6, height:6, borderRadius:'50%',
                    background: pc, flexShrink:0 }} />
                  <span style={{ fontSize:12, fontWeight: isActive ? 600 : 400,
                    color: isActive ? TX : TX2, minWidth:0, overflow:'hidden',
                    textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    {uc.usecase_name}
                  </span>
                </div>
                <div style={{ display:'flex', gap:6, marginTop:4, paddingLeft:12 }}>
                  <span style={{ fontSize:9, color: TX3 }}>
                    {PURPOSE_LABEL[uc.run_purpose] || uc.run_purpose}
                  </span>
                  <span style={{ fontSize:9, color: TX3 }}>·</span>
                  <span style={{ fontSize:9, color: TX3 }}>{uc.run_count} run{uc.run_count !== 1 ? 's' : ''}</span>
                  {uc.champion_run_id && (
                    <span style={{ fontSize:9, color: GOLD }}>★</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* ── Tree panel ──────────────────────────────────────────── */}
        <div style={{ flex:'0 0 360px', borderRight:`1px solid ${BD}`,
          overflowY:'auto', padding:'14px 14px' }}>
          {!activeUc ? (
            <div style={{ color: TX3, fontSize:11, padding:20, textAlign:'center' }}>
              Select a usecase
            </div>
          ) : lineageLoad ? (
            <div style={{ display:'flex', justifyContent:'center', padding:40 }}>
              <Spinner size={22} />
            </div>
          ) : trees.length === 0 ? (
            <div style={{ color: TX3, fontSize:11, padding:20, textAlign:'center' }}>
              No runs in this usecase yet.
            </div>
          ) : (
            <>
              {/* Usecase header */}
              <div style={{ marginBottom:14, padding:'10px 12px', borderRadius:6,
                background: BG3, border:`1px solid ${BD}` }}>
                <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                  <span style={{ width:8, height:8, borderRadius:'50%', flexShrink:0,
                    background: PURPOSE_COLOR[activeUc.run_purpose] || '#6b7280' }} />
                  <span style={{ fontWeight:700, fontSize:13 }}>{activeUc.usecase_name}</span>
                </div>
                <div style={{ display:'flex', gap:12, paddingLeft:16, flexWrap:'wrap' }}>
                  <span style={{ fontSize:9, color: TX3 }}>
                    {PURPOSE_LABEL[activeUc.run_purpose]}
                  </span>
                  <span style={{ fontSize:9, color: TX3 }}>
                    {lineage?.runs?.length || 0} experiments
                  </span>
                  {activeUc.champion_run_id && (
                    <span style={{ fontSize:9, color: GOLD }}>★ champion set</span>
                  )}
                </div>
              </div>
              {renderTree(trees, selectedRun?.id, setSelectedRun)}
            </>
          )}
        </div>

        {/* ── Detail panel ────────────────────────────────────────── */}
        <div style={{ flex:1, minWidth:0, overflowY:'auto', background: BG3 }}>
          <DetailPanel
            run={selectedRun}
            usecase={activeUc}
            onChampion={handleChampion}
            onUnchampion={handleUnchampion}
            canChampion={!championing && (user?.role === "ADMIN" || user?.role === "ML_ENGINEER" || user?.role === "ORG_ADMIN")}
          />
        </div>

      </div>

      {showCreate && (
        <CreateUsecaseModal
          projectId={projectId}
          onClose={() => setShowCreate(false)}
          onCreated={(uc) => {
            setShowCreate(false);
            setUsecases(prev => [...(prev || []), { ...uc, run_count: 0 }]);
            setActiveUc(uc);
          }}
        />
      )}
    </div>
  );
}
